/**
 * Pure SVG icon registry for the service pages.
 *
 * Each icon is a stateless component that forwards sizing/stroke props so the
 * same glyph can serve hero (96-120px) and advantage-card (48px) contexts.
 * Keys are short strings: 'pad', 'screen', 'assembly', 'precision',
 * 'multicolor', 'durable', 'speed', 'materials'.
 */

const baseProps = {
    xmlns: 'http://www.w3.org/2000/svg',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'aria-hidden': 'true',
    focusable: 'false'
};

function PadIcon({ size = 96, strokeWidth = 1.5 }) {
    // Pad printing: a C-frame press. The silicone pad hangs from the arm above a
    // moulded part whose face carries a printed frame mark. Asymmetric on purpose,
    // so the stack never reads as a face at hero scale.
    return (
        <svg {...baseProps} width={size} height={size} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
            <path d="M2.5 21h19" />
            <path d="M19.5 21V3.5H11v2.25" />
            <path d="M7.5 5.75h7V7.5a3.5 3.5 0 0 1-7 0z" />
            <path d="M11 12.4v.9" />
            <path d="M5 21v-4.75a1.75 1.75 0 0 1 1.75-1.75h8.5A1.75 1.75 0 0 1 17 16.25V21" />
            <rect x="9.6" y="16.6" width="2.8" height="2.6" rx="0.7" />
        </svg>
    );
}

function ScreenIcon({ size = 96, strokeWidth = 1.5 }) {
    // Screen printing: a squeegee leaning into its stroke across the mesh frame
    // (dashed: the stencil window), and the flat panel below carrying a printed scale.
    return (
        <svg {...baseProps} width={size} height={size} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 5.5h5.5" />
            <path d="m8 4 1.5 1.5L8 7" />
            <path d="M12.5 8.5 16.5 3.5" />
            <path d="m15 2.25 3 2.5" />
            <rect x="2.5" y="8.5" width="19" height="6.5" rx="1" />
            <rect x="7" y="10.5" width="10" height="2.5" rx="0.5" strokeDasharray="1.25 1.25" />
            <path d="M3 21h18" />
            <path d="M6.5 18.25V21M10 19.25V21M14 19.25V21M17.5 18.25V21" />
        </svg>
    );
}

function AssemblyIcon({ size = 96, strokeWidth = 1.5 }) {
    // Assembly: a tabbed part sliding into a slotted housing.
    return (
        <svg {...baseProps} width={size} height={size} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 7.5h7v3h2.5v3H10v3H3z" />
            <path d="M21 7.5h-6.5v3H16v3h-1.5v3H21z" />
            <path d="M6 4.25h3.5" />
            <path d="m8.25 2.75 1.5 1.5-1.5 1.5" />
            <path d="M18 19.75h-3.5" />
            <path d="m15.75 18.25-1.5 1.5 1.5 1.5" />
        </svg>
    );
}

function PrecisionIcon({ size = 48, strokeWidth = 1.5 }) {
    return (
        <svg {...baseProps} width={size} height={size} strokeWidth={strokeWidth}>
            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
        </svg>
    );
}

function MulticolorIcon({ size = 48, strokeWidth = 1.5 }) {
    return (
        <svg {...baseProps} width={size} height={size} strokeWidth={strokeWidth}>
            <circle cx="12" cy="12" r="3" />
            <circle cx="12" cy="5" r="2" />
            <circle cx="19" cy="12" r="2" />
            <circle cx="12" cy="19" r="2" />
            <circle cx="5" cy="12" r="2" />
        </svg>
    );
}

function DurableIcon({ size = 48, strokeWidth = 1.5 }) {
    return (
        <svg {...baseProps} width={size} height={size} strokeWidth={strokeWidth}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
    );
}

function SpeedIcon({ size = 48, strokeWidth = 1.5 }) {
    return (
        <svg {...baseProps} width={size} height={size} strokeWidth={strokeWidth}>
            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
        </svg>
    );
}

function MaterialsIcon({ size = 48, strokeWidth = 1.5 }) {
    return (
        <svg {...baseProps} width={size} height={size} strokeWidth={strokeWidth}>
            <rect x="2" y="6" width="20" height="12" rx="2" />
            <path d="M12 12h.01" />
            <path d="M17 12h.01" />
            <path d="M7 12h.01" />
        </svg>
    );
}

const ICONS = {
    pad: PadIcon,
    screen: ScreenIcon,
    assembly: AssemblyIcon,
    precision: PrecisionIcon,
    multicolor: MulticolorIcon,
    durable: DurableIcon,
    speed: SpeedIcon,
    materials: MaterialsIcon
};

/**
 * Returns the icon component registered under `key`, or null when unknown.
 *
 * @param {string} key - Short icon key (e.g. 'pad', 'precision').
 * @returns {Function|null} A pure SVG component, or null if not found.
 */
// eslint-disable-next-line react-refresh/only-export-components -- registry module, not a fast-refresh component surface
export function getIcon(key) {
    return ICONS[key] || null;
}

export default ICONS;
